import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { CSSProperties, ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { rewardsOverviewToSimpleRewards } from '@sorare/core/src/lib/rewards';
import {
  laptopAndAbove,
  laptopAndBelow,
} from '@sorare/core/src/style/mediaQuery';

import { DivisionChangeConfigBar } from 'components/leaderboard/DivisionChangeConfigBar';
import { ThresholdProgressBar } from 'components/leaderboard/ThresholdProgressBar';
import { LineupButtons } from 'components/lineup/LineupButtons';
import { RewardOverview } from 'components/rewards/RewardOverview';
import { Variant } from 'components/rewards/RewardOverview/types';
import {
  ContenderStatus,
  useContenderStatus,
} from 'hooks/so5/useContenderStatus';
import { LineupFooter } from 'layouts/LineupLayout/LineupFooter';
import { LineupRanking } from 'layouts/LineupLayout/LineupRanking';
import { LineupStatus } from 'layouts/LineupLayout/LineupStatus';
import { isFixtureOpened, isFixtureStarted } from 'lib/so5';

import { TeamLineupFooter_so5LeaderboardContender } from './__generated__/index.graphql';

const StyledVertical = styled(Vertical)`
  &.withSpacing {
    margin-bottom: var(--quadruple-unit);
  }

  @media ${laptopAndBelow} {
    margin-bottom: var(--quadruple-unit);
  }
`;
const StatusWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--c-nd-50);
  border-radius: var(--double-unit);

  &.NOT_STARTED {
    border-color: #4796da;
  }
  &.PROMOTED {
    border-color: #048a3d;
  }
  &.RELEGATED {
    border-color: #c22342;
  }
  &.INCOMPLETE {
    border-color: var(--c-yellow-500);
  }
  &.withTargetBorder {
    border-color: var(--target-color);
  }
`;
const StyledLineupStatus = styled(LineupStatus)`
  min-height: 95px;
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    &,
    &.isUpcoming {
      min-height: 160px;
      padding: var(--quadruple-unit) var(--double-unit);
    }
  }

  @media ${laptopAndBelow} {
    &.isUpcoming {
      min-height: inherit;
      padding: var(--double-unit);
    }
  }
`;
const StyledSBHorizontal = styled(SBHorizontal)`
  padding: var(--unit) var(--double-unit);
`;
const StyledHorizontal = styled(Horizontal)`
  padding: var(--unit) var(--double-unit);
`;
const StyledButtonBase = styled(ButtonBase)`
  width: 100%;
  min-height: calc(6 * var(--unit));
`;

type Props = {
  so5LeaderboardContender: TeamLineupFooter_so5LeaderboardContender;
  onClickPrizePool?: () => void;
  onClickSchedule?: () => void;
} & Pick<ComponentProps<typeof LineupButtons>, 'onLoadPreviousLineup'>;

export const TeamLineupFooter = ({
  so5LeaderboardContender,
  onClickPrizePool,
  onClickSchedule,
  onLoadPreviousLineup,
}: Props) => {
  const navigate = useNavigate();
  const { generateSportContextPath } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();
  const { so5Leaderboard, so5Lineup, managerTeam } = so5LeaderboardContender;
  const { status, divisionChange } =
    useContenderStatus(so5LeaderboardContender) || {};
  const isUpcoming = isFixtureOpened(so5Leaderboard.so5Fixture);
  const isLive = isFixtureStarted(so5Leaderboard.so5Fixture);

  const simpleRewards = rewardsOverviewToSimpleRewards({
    rewardsOverview: so5Leaderboard.totalRewards,
    rarity: so5Leaderboard.mainRarityType,
    onePerCategoryOnly: true,
  }).filter(reward => reward.count > 0);

  const hasDivisionChangeConfiguration =
    !!so5Leaderboard.divisionChangeConfiguration;

  const ranking = so5Lineup?.so5Rankings.at(0);

  const buttons = (
    <LineupButtons
      standalone
      so5Leaderboard={so5Leaderboard}
      so5Lineup={so5Lineup}
      managerTeam={managerTeam}
      onDelete={() => {
        navigate(
          generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
            params: {
              eventType:
                so5LeaderboardContender.so5Leaderboard.so5Fixture.type.toLowerCase(),
              fixture: so5LeaderboardContender.so5Leaderboard.so5Fixture.slug,
            },
          })
        );
      }}
      onLoadPreviousLineup={onLoadPreviousLineup}
    />
  );

  if (isUpcoming && isLaptopAndAbove) {
    if (
      status &&
      [ContenderStatus.DIVISION_CHANGE, ContenderStatus.INCOMPLETE].includes(
        status
      )
    ) {
      return (
        <StyledLineupStatus so5LeaderboardContender={so5LeaderboardContender}>
          <Container>{buttons}</Container>
        </StyledLineupStatus>
      );
    }
    return (
      <Container>
        <StyledVertical className="withSpacing">{buttons}</StyledVertical>
      </Container>
    );
  }
  if (isUpcoming) {
    return (
      <Container>
        <StyledVertical
          gap={3}
          className={classNames({
            withSpacing: status === ContenderStatus.DIVISION_CHANGE,
          })}
        >
          <StatusWrapper className={classNames(divisionChange, status)}>
            <ConditionalWrapper
              Wrapper={StyledButtonBase}
              wrap={
                !!status &&
                [ContenderStatus.EMPTY, ContenderStatus.NOT_STARTED].includes(
                  status
                )
              }
              props={{
                onClick:
                  status === ContenderStatus.NOT_STARTED
                    ? onClickSchedule
                    : onClickPrizePool,
              }}
            >
              <StyledLineupStatus
                so5LeaderboardContender={so5LeaderboardContender}
              />
            </ConditionalWrapper>
            {ranking?.targetedRewardWithEligibility?.reward.target ? (
              <StyledButtonBase onClick={onClickPrizePool}>
                <StyledHorizontal>
                  <img
                    src={
                      ranking.targetedRewardWithEligibility.reward.target
                        .pictureUrl
                    }
                    alt=""
                    width={12}
                    height={12}
                  />
                  <Horizontal>
                    <BodyM bold>
                      <FormattedMessage
                        id="TeamLineupFooter.nameTarget"
                        defaultMessage="{name} Target"
                        values={{
                          name: ranking.targetedRewardWithEligibility.reward
                            .target.name,
                        }}
                      />
                    </BodyM>
                    <BodyM color="var(--c-nd-600)">
                      <FormattedMessage {...fantasy.score} /> {'≥'}{' '}
                      {ranking.targetedRewardWithEligibility.reward.score}
                    </BodyM>
                  </Horizontal>
                </StyledHorizontal>
              </StyledButtonBase>
            ) : (
              <>
                {status &&
                  ![ContenderStatus.TARGET, ContenderStatus.EMPTY].includes(
                    status
                  ) && (
                    <StyledButtonBase onClick={onClickPrizePool}>
                      <StyledSBHorizontal>
                        <BodyM bold>
                          <FormattedMessage {...fantasy.viewPrizePool} />
                        </BodyM>
                        <Horizontal>
                          {simpleRewards.map((simpleReward, i) => (
                            <RewardOverview
                              // eslint-disable-next-line react/no-array-index-key
                              key={i}
                              size={3}
                              variant={Variant.IMAGE_ONLY}
                              simpleReward={simpleReward}
                            />
                          ))}
                        </Horizontal>
                      </StyledSBHorizontal>
                    </StyledButtonBase>
                  )}
              </>
            )}
            {status === ContenderStatus.TARGET &&
              ranking?.targetedRewardWithEligibility?.reward.target && (
                <StyledButtonBase onClick={onClickPrizePool}>
                  <Horizontal>
                    <img
                      src={
                        ranking.targetedRewardWithEligibility.reward.target
                          .pictureUrl
                      }
                      alt=""
                      width={12}
                      height={12}
                    />
                    <Horizontal>
                      <BodyM bold>
                        <FormattedMessage
                          id="TeamLineupFooter.nameTarget"
                          defaultMessage="{name} Target"
                          values={{
                            name: ranking.targetedRewardWithEligibility.reward
                              .target.name,
                          }}
                        />
                      </BodyM>
                      <BodyM color="var(--c-nd-600)">
                        <FormattedMessage {...fantasy.score} /> {'≥'}{' '}
                        {ranking.targetedRewardWithEligibility.reward.score}
                      </BodyM>
                    </Horizontal>
                  </Horizontal>
                </StyledButtonBase>
              )}
          </StatusWrapper>
          {buttons}
        </StyledVertical>
      </Container>
    );
  }

  return (
    <ConditionalWrapper wrap={!isLaptopAndAbove} Wrapper={Container}>
      <ConditionalWrapper
        wrap={!isLaptopAndAbove}
        Wrapper={StyledButtonBase}
        props={{
          onClick:
            status === ContenderStatus.NOT_STARTED
              ? onClickSchedule
              : onClickPrizePool,
        }}
      >
        <StyledVertical
          gap={isLaptopAndAbove ? 3 : 2}
          className={classNames({
            withSpacing: status === ContenderStatus.INCOMPLETE,
          })}
        >
          <ConditionalWrapper
            wrap={!isLaptopAndAbove}
            Wrapper={StatusWrapper}
            props={{
              className: classNames(divisionChange, status, {
                withTargetBorder:
                  isLive && ranking?.targetedRewardWithEligibility?.eligible,
              }),
              style: {
                '--target-color':
                  ranking?.targetedRewardWithEligibility?.reward.target?.color,
              } as CSSProperties,
            }}
          >
            <StyledLineupStatus
              so5LeaderboardContender={so5LeaderboardContender}
              message={
                isLaptopAndAbove &&
                status === ContenderStatus.NOT_STARTED && (
                  <FormattedMessage {...glossary.waitingForMatches} />
                )
              }
            >
              <Container>
                <LineupRanking
                  so5LeaderboardContender={so5LeaderboardContender}
                />
              </Container>
            </StyledLineupStatus>
            {!isLaptopAndAbove && (
              <LineupFooter
                so5LeaderboardContender={so5LeaderboardContender}
                onLoadPreviousLineup={onLoadPreviousLineup}
              />
            )}
          </ConditionalWrapper>
          {!isLaptopAndAbove && hasDivisionChangeConfiguration && (
            <DivisionChangeConfigBar
              so5Leaderboard={so5Leaderboard}
              so5Lineup={so5Lineup}
            />
          )}
          {!isLaptopAndAbove && status === ContenderStatus.THRESHOLD && (
            <ThresholdProgressBar
              so5Leaderboard={so5Leaderboard}
              so5Lineup={so5Lineup}
            />
          )}
        </StyledVertical>
      </ConditionalWrapper>
    </ConditionalWrapper>
  );
};

TeamLineupFooter.fragments = {
  so5LeaderboardContender: gql`
    fragment TeamLineupFooter_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        mainRarityType
        so5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
          ...isFixtureStarted_so5Fixture
        }
        totalRewards {
          ...rewardsOverviewToSimpleRewards_rewardsOverview
        }
        divisionChangeConfiguration {
          id
          promotionRankBasisPoint
        }
        ...LineupButtons_so5Leaderboard
        ...ThresholdProgressBar_so5Leaderboard
        ...DivisionChangeConfigBar_so5Leaderboard
      }
      so5Lineup {
        id
        so5Rankings {
          id
          targetedRewardWithEligibility {
            reward {
              score
              target {
                slug
                name
                color
                pictureUrl
              }
            }
          }
        }
        ...LineupButtons_so5Lineup
        ...ThresholdProgressBar_so5Lineup
        ...DivisionChangeConfigBar_so5Lineup
      }
      managerTeam {
        id
        ...LineupButtons_managerTeam
      }
      ...useContenderStatus_so5LeaderboardContender
      ...LineupFooter_so5LeaderboardContender
      ...LineupRanking_so5LeaderboardContender
      ...LineupStatus_so5LeaderboardContender
    }
    ${useContenderStatus.fragments.so5LeaderboardContender}
    ${isFixtureOpened.fragments.so5Fixture}
    ${isFixtureStarted.fragments.so5Fixture}
    ${LineupButtons.fragments.so5Leaderboard}
    ${LineupButtons.fragments.so5Lineup}
    ${LineupButtons.fragments.managerTeam}
    ${DivisionChangeConfigBar.fragments.so5Leaderboard}
    ${DivisionChangeConfigBar.fragments.so5Lineup}
    ${ThresholdProgressBar.fragments.so5Leaderboard}
    ${ThresholdProgressBar.fragments.so5Lineup}
    ${rewardsOverviewToSimpleRewards.fragments.rewardsOverview}
    ${LineupFooter.fragments.so5LeaderboardContender}
    ${LineupRanking.fragments.so5LeaderboardContender}
    ${LineupStatus.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<TeamLineupFooter_so5LeaderboardContender>,
};
